import './address.scss';
import 'react-calendar/dist/Calendar.css';

import { useState } from 'react';
import { useSelector } from 'react-redux';

import AskCpfModal from '@/resources/components/askCpfModal/AskCpfModal';
import { useShipmentContext } from '@/resources/components/cart/shipment/Shipment';
import { useCartContext } from '@/resources/hooks/useCartContext';
import { useOrderContext } from '@/resources/hooks/useOrderContext';
import { useWebContext } from '@/resources/hooks/useWebContext';
import { setShipmentAddress } from '@/routes/order';
import { getOrderMinValue } from '@/services/global';
import { getDeliveryInfo } from '@/services/order';
import { ApplicationState } from '@/store';

import { IOrder } from '../../../../../store/ducks/order/types';
import AddressModal from '../../../global/address/AddressModal';
import { IAddressModalStep } from '../../../global/address/components/types';
import OrderResume from '../../orderResume/OrderResume';
import Subtotal from '../../subtotal/Subtotal';
import FooterDefaultButton from '../footerDefaultButton/FooterDefaultButton';
import { ShipmentCard } from '../shipmentCard/ShipmentCard';
import DeliveryDiscountAlert from './components/delivery/DeliveryDiscountAlert';
import DeliveryInformation from './components/delivery/DeliveryInformation';
import RenderOrderAddress from './components/RenderOrderAddress';
import RenderOrderType from './components/RenderOrderType';

interface Props {
  order: IOrder;
}

function Address({ order }: Props) {
  const { shipment, shop_name_dst, id, cart } = order;
  const { step } = useShipmentContext();
  const { configs } = useWebContext();
  const { useFunction } = useOrderContext();
  const { setMinOrderAmount, setWebappOutOfDelivery, webapp_out_of_delivery, min_order_amount } =
    useCartContext();

  const isOverMinOrder = order.cart.total > getOrderMinValue(order, min_order_amount);

  const [show, setShowAddress] = useState<boolean>(false);
  const [modalStep, setModalStep] = useState<IAddressModalStep>('type');
  const [showCpf, setShowCpf] = useState<boolean>(false);

  const [calcButtonDisabled, setCalcButtonDisabled] = useState<boolean>(false);

  const selectCPFAnswered = useSelector<ApplicationState, boolean>(
    (state) => state.client.cpfAnswered,
  );

  const deliveryInfo = getDeliveryInfo(order.shipment);
  const invalidDelivery =
    order.shipment?.type === 'delivery' && !deliveryInfo && !order.shipment?.refresh_carrier;

  const paymentAvailable = !(step <= 2 || !order.shipment.pickup_at || invalidDelivery);

  const setCheckout = useFunction('proceedToCheckout') ?? (() => {});

  const handleChooseSchedule = () => {
    setModalStep('scheduling');
    setShowAddress(true);
  };

  const setShow = (show: boolean) => {
    if (!show) {
      setShowCpf(false);
      setShowAddress(show);

      return;
    }

    if (configs.ask_cpf && !selectCPFAnswered && !order?.client.cpf) {
      setShowCpf(true);
      return;
    }

    setShowAddress(show);
  };

  const handleRefreshShipment = () => {
    setCalcButtonDisabled(true);

    setShipmentAddress(id, shipment.address.id, { type: 'delivery' })
      .then((order) => {
        setMinOrderAmount(order?.shipment?.min_order_amount ?? 0);

        setWebappOutOfDelivery(!!order?.shipment?.webapp_out_of_delivery);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setCalcButtonDisabled(false));
  };

  const showPickupAt = shipment.type === 'shop' || shipment.delivery?.type === 'google-maps';

  const shouldRefreshCarrier =
    !!shipment.refresh_carrier &&
    (!!shipment.pickup_at || !showPickupAt) &&
    shipment.address &&
    isOverMinOrder;

  return (
    <>
      <ShipmentCard
        showModal={show}
        disabled={step <= 1 || order?.client?.type === 3}
        setShowModal={setShow}
        warningType="forma de entrega"
        handleCloseModal={() => setModalStep('type')}
      >
        <ShipmentCard.Content
          title="2 - Entrega"
          actions={!!shipment.address}
          footer={
            <>
              {!shipment.address && (
                <FooterDefaultButton title="Definir entrega" showModal={setShow} />
              )}

              {((shipment.type === 'delivery' && shipment.address) || webapp_out_of_delivery) && (
                <DeliveryInformation fromPayment />
              )}

              {!shipment.pickup_at && showPickupAt && isOverMinOrder && (
                <FooterDefaultButton
                  title="Definir horário"
                  showModal={setShow}
                  onClick={() => handleChooseSchedule()}
                />
              )}

              {shouldRefreshCarrier && (
                <FooterDefaultButton
                  title="Calcular entrega"
                  onClick={handleRefreshShipment}
                  isDisabled={calcButtonDisabled}
                />
              )}
              <div className="pt-4">
                <OrderResume order={order} />
                <Subtotal cart={cart} />
                {paymentAvailable && !order.is_paid && (
                  <FooterDefaultButton
                    title="Ir para o pagamento"
                    onClick={() => {
                      setCheckout();
                    }}
                  />
                )}
              </div>
            </>
          }
        >
          <div className="d-flex flex-column">
            {shipment.type && !webapp_out_of_delivery && <RenderOrderType type={shipment.type} />}

            {shipment.address && (
              <RenderOrderAddress
                type={shipment.type}
                address={shipment.address}
                shopNameDst={shop_name_dst}
                pickupAt={shipment.pickup_at}
                fromPayment
              />
            )}
          </div>
        </ShipmentCard.Content>

        <AddressModal
          showModal={show}
          step={modalStep}
          showSchedule={true}
          setShowModal={setShow}
          handleClose={() => setModalStep('type')}
        />

        <AskCpfModal show={showCpf} setShow={setShowCpf} setAddress={setShowAddress} />
      </ShipmentCard>

      <DeliveryDiscountAlert />
    </>
  );
}

export default Address;
